import { useCallback } from "react";
import { getCategoryLabel } from "./aiMappingColors";
import { geoToPixel } from "./aiMappingGeo";
import { getAreaGeoContext } from "./aiMappingDetections";
import { classifyAiMappingError } from "./aiMappingErrors";
import { handleAiMappingMutationFailure } from "./aiMappingControllerUtils";
import { AiMappingNotify } from "./aiMappingControllerTypes";
import { AiMappingAreaLike, AiMappingMapDriver, AiMappingResultsLike } from "./aiMappingTypes";
import {
  useCreateAiMappingDetection,
  useDeleteAiMappingDetection,
  useRedoAiMappingRunArea,
  useUndoAiMappingRunArea,
  useUpdateAiMappingDetection,
} from "./hooks";

interface UseAiMappingDetectionHandlersOptions {
  busy: boolean;
  quickAddCategory: string | null;
  quickAddAreaId: string | null;
  unmountedRef: React.MutableRefObject<boolean>;
  quickAddQueueRef: React.MutableRefObject<Promise<void>>;
  setBusy: React.Dispatch<React.SetStateAction<boolean>>;
  setQuickAddCategory: React.Dispatch<React.SetStateAction<string | null>>;
  setQuickAddAreaId: React.Dispatch<React.SetStateAction<string | null>>;
  setSelectedDetectionId: React.Dispatch<React.SetStateAction<string | null>>;
  applyResults: (payload: AiMappingResultsLike) => void;
  refetchResults: (areaId: string) => Promise<void> | Promise<AiMappingResultsLike | null>;
  getAreaById: (areaId: string) => AiMappingAreaLike | null;
  driver: AiMappingMapDriver | null;
  notify: AiMappingNotify;
  handleError: (error: unknown, options?: { inline?: boolean }) => boolean;
}

export function useAiMappingDetectionHandlers({
  busy,
  quickAddCategory,
  quickAddAreaId,
  unmountedRef,
  quickAddQueueRef,
  setBusy,
  setQuickAddCategory,
  setQuickAddAreaId,
  setSelectedDetectionId,
  applyResults,
  refetchResults,
  getAreaById,
  driver,
  notify,
  handleError,
}: UseAiMappingDetectionHandlersOptions) {
  const [updateDetectionAction] = useUpdateAiMappingDetection();
  const [deleteDetectionAction] = useDeleteAiMappingDetection();
  const [createDetectionAction] = useCreateAiMappingDetection();
  const [undoAction] = useUndoAiMappingRunArea();
  const [redoAction] = useRedoAiMappingRunArea();

  const updateDetection = useCallback(
    async (
      areaId: string,
      detectionId: string,
      changes: { category?: string; color?: string | null; confidence?: number }
    ) => {
      if (busy) return;
      setBusy(true);
      const result = await updateDetectionAction({
        variables: {
          input: {
            runAreaId: areaId,
            detectionId,
            category: changes.category,
            color: changes.color ?? null,
            confidence: changes.confidence,
          },
        },
      });
      if (!result.success) {
        await handleAiMappingMutationFailure(result, {
          notify,
          handleError,
          unmountedRef,
          staleCodes: ["detection_not_found", "unknown_category"],
          onStale: async () => {
            await refetchResults(areaId);
          },
        });
      } else {
        const bundle = result.data?.updateAiMappingDetection as AiMappingResultsLike | undefined;
        if (bundle && !unmountedRef.current) {
          applyResults(bundle);
          notify("Detection updated", "success");
        }
      }
      if (!unmountedRef.current) setBusy(false);
    },
    [
      busy,
      updateDetectionAction,
      applyResults,
      refetchResults,
      handleError,
      notify,
      setBusy,
      unmountedRef,
    ]
  );

  const deleteDetection = useCallback(
    async (areaId: string, detectionId: string) => {
      if (busy) return;
      setBusy(true);
      const result = await deleteDetectionAction({
        variables: { runAreaId: areaId, detectionId },
      });
      if (!result.success) {
        await handleAiMappingMutationFailure(result, {
          notify,
          handleError,
          unmountedRef,
          staleCodes: ["detection_not_found"],
          onStale: async () => {
            await refetchResults(areaId);
          },
        });
      } else {
        const bundle = result.data?.deleteAiMappingDetection as AiMappingResultsLike | undefined;
        if (bundle && !unmountedRef.current) {
          applyResults(bundle);
          notify("Detection deleted", "success");
        }
      }
      if (!unmountedRef.current) setBusy(false);
    },
    [
      busy,
      deleteDetectionAction,
      applyResults,
      refetchResults,
      handleError,
      notify,
      setBusy,
      unmountedRef,
    ]
  );

  const undoArea = useCallback(
    async (areaId: string) => {
      if (busy) return;
      setBusy(true);
      const result = await undoAction({ variables: { id: areaId } });
      if (result.success) {
        const bundle = result.data?.undoAiMappingRunArea as AiMappingResultsLike | undefined;
        if (bundle && !unmountedRef.current) applyResults(bundle);
      } else if (!unmountedRef.current) {
        handleError(result);
      }
      if (!unmountedRef.current) setBusy(false);
    },
    [busy, undoAction, applyResults, handleError, setBusy, unmountedRef]
  );

  const redoArea = useCallback(
    async (areaId: string) => {
      if (busy) return;
      setBusy(true);
      const result = await redoAction({ variables: { id: areaId } });
      if (result.success) {
        const bundle = result.data?.redoAiMappingRunArea as AiMappingResultsLike | undefined;
        if (bundle && !unmountedRef.current) applyResults(bundle);
      } else if (!unmountedRef.current) {
        handleError(result);
      }
      if (!unmountedRef.current) setBusy(false);
    },
    [busy, redoAction, applyResults, handleError, setBusy, unmountedRef]
  );

  const selectDetection = useCallback(
    (detectionId: string | null) => {
      setSelectedDetectionId(detectionId);
    },
    [setSelectedDetectionId]
  );

  const cancelQuickAdd = useCallback(() => {
    setQuickAddCategory(null);
    setQuickAddAreaId(null);
    driver?.setClickHandler(null);
  }, [driver, setQuickAddCategory, setQuickAddAreaId]);

  const toggleQuickAdd = useCallback(
    (areaId: string, category: string) => {
      if (quickAddCategory === category && quickAddAreaId === areaId) {
        cancelQuickAdd();
        return;
      }
      const area = getAreaById(areaId);
      const context = getAreaGeoContext(area);
      if (!context.imageWidth || !context.imageHeight || !context.bounds) {
        notify("Run detection on this area first before adding detections.", "warning");
        return;
      }
      setQuickAddCategory(category);
      setQuickAddAreaId(areaId);
      notify(
        `Click the map to add ${getCategoryLabel(
          category
        )} detections. Press the “+” again to stop.`,
        "info"
      );
      driver?.setClickHandler((latLng) => {
        if (!context.imageWidth || !context.imageHeight || !context.bounds) return;
        const pixel = geoToPixel(
          latLng[0],
          latLng[1],
          context.imageWidth,
          context.imageHeight,
          context.bounds
        );
        if (!pixel) return;
        const half = Math.max(
          6,
          Math.round(Math.min(context.imageWidth, context.imageHeight) * 0.02)
        );
        const cx = Math.min(Math.max(pixel[0], half), context.imageWidth - half);
        const cy = Math.min(Math.max(pixel[1], half), context.imageHeight - half);
        const bbox = [cx - half, cy - half, half * 2, half * 2];
        quickAddQueueRef.current = quickAddQueueRef.current.then(async () => {
          const result = await createDetectionAction({
            variables: { input: { runAreaId: areaId, category, bbox } },
          });
          const bundle = result.data?.createAiMappingDetection as AiMappingResultsLike | undefined;
          if (bundle && !unmountedRef.current) applyResults(bundle);
        });
      });
    },
    [
      quickAddCategory,
      quickAddAreaId,
      getAreaById,
      cancelQuickAdd,
      notify,
      driver,
      createDetectionAction,
      applyResults,
      setQuickAddCategory,
      setQuickAddAreaId,
      quickAddQueueRef,
      unmountedRef,
    ]
  );

  return {
    updateDetection,
    deleteDetection,
    undoArea,
    redoArea,
    selectDetection,
    cancelQuickAdd,
    toggleQuickAdd,
    refetchResults,
  };
}
